import { format, parseISO } from 'date-fns';
import type { WorkType } from '../../shared/types';

const inr = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

const inrPrecise = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Indian digit grouping (1,23,456) — the reason for an explicit en-IN locale. */
export function formatINR(amount: number, precise = false): string {
  return (precise ? inrPrecise : inr).format(amount || 0);
}

export const formatDayMonth = (iso: string): string => format(parseISO(iso), 'dd MMM');
export const formatWeekday = (iso: string): string => format(parseISO(iso), 'EEE');
export const formatFullDate = (iso: string): string => format(parseISO(iso), 'd MMMM yyyy');

/** Turns a bucket key from /api/summary into an axis label. */
export function formatBucketKey(key: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(key)) return format(parseISO(key), 'd MMM');
  if (/^\d{4}-\d{2}$/.test(key)) return format(parseISO(`${key}-01`), 'MMM');
  return key;
}

/** Each work type gets a stable tint so the badges read as a legend. */
export const WORK_TYPE_STYLE: Record<WorkType, { bg: string; fg: string }> = {
  Plan: { bg: 'var(--color-blue-100)', fg: 'var(--color-blue-700)' },
  Design: { bg: 'var(--color-coral-100)', fg: 'var(--color-coral-700)' },
  Dev: { bg: 'var(--color-cyan-100)', fg: 'var(--color-cyan-600)' },
  Multi: { bg: 'var(--color-gold-100)', fg: 'var(--color-gold-600)' },
  Documentation: { bg: '#ece7fb', fg: '#5b46a8' },
  Meeting: { bg: '#e6f4ea', fg: '#2f7a48' },
  Other: { bg: 'var(--color-paper-sunk)', fg: 'var(--color-ink-soft)' },
};

/**
 * Project identity colours. Validated with the dataviz palette checker in
 * light mode: lightness band, chroma floor, normal-vision separation and
 * contrast all pass; the worst adjacent CVD pair sits at deltaE 7.7, which is
 * acceptable only because every swatch in this app is shown beside the project
 * name — colour never carries identity on its own.
 */
export const PROJECT_COLORS = [
  '#3B62C4',
  '#E9576B',
  '#0D8FA6',
  '#A97A1F',
  '#6A4CB8',
  '#2F7A48',
  '#B0567F',
];

/** Single-series mark colours. Each chart uses one, so no categorical set is needed. */
export const MARK = {
  hours: '#3B62C4',
  money: '#E9576B',
  type: '#0D8FA6',
  grid: '#E7E0D7',
  axis: '#7C8AA3',
} as const;
